import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Labeled, Select, TextInput } from '@tes/ui';
import { DATE_FORMAT_OPTIONS, SETTINGS_LIMITS, type OperationalSettings } from '@tes/domain';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import { formatDateTime } from '../lib/formatDate';
import { NUMBER_SETTINGS, numberSettingValue, settingsPatch, type SettingsDraft } from '../lib/settingsForm';

const card = 'rounded-xl border border-slate-200 bg-white p-5';
const commonZones = ['UTC', 'Australia/Perth', 'Australia/Darwin', 'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Hobart', 'Pacific/Auckland', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Singapore'];
const supportedZones = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
const zones = [...new Set([...commonZones, ...supportedZones])].sort();

export function OperationalSettingsForm({ settings }: { settings: OperationalSettings }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<SettingsDraft>({});
  const [saved, setSaved] = useState(false);
  const result = settingsPatch(draft);
  const dirty = Object.keys(draft).length > 0;
  const save = useMutation({
    mutationFn: () => api.patchSettings(result.patch),
    onSuccess: (updated) => {
      qc.setQueryData(['settings'], updated);
      setDraft({});
      setSaved(true);
    },
  });
  const edit = (key: keyof SettingsDraft, value: string, original: string) => {
    setSaved(false);
    save.reset();
    setDraft((current) => {
      const next = { ...current };
      if (value === original) delete next[key];
      else next[key] = value;
      return next;
    });
  };
  const dates = {
    timezone: draft.timezone ?? settings.platform.timezone,
    dateFormat: draft.dateFormat ?? settings.platform.dateFormat,
  };
  const retentionReduced = draft.retentionDays !== undefined && Number(draft.retentionDays) < settings.signing.retentionDays;
  const uploadReduced = draft.uploadTtlHours !== undefined && Number(draft.uploadTtlHours) < settings.uploads.uploadTtlHours;

  const numberFields = (group: 'signing' | 'uploads' | 'webhooks') => (
    <div className="grid gap-4 md:grid-cols-2">
      {NUMBER_SETTINGS.filter((field) => field.group === group).map((field) => {
        const original = String(numberSettingValue(settings, field));
        const limits = SETTINGS_LIMITS[field.key];
        return (
          <Labeled key={field.key} label={field.label} hint={`${field.hint} Range: ${limits.min}–${limits.max}.`}>
            <TextInput type="number" min={limits.min} max={limits.max} step={1} required
              value={draft[field.key] ?? original}
              onChange={(event) => edit(field.key, event.target.value, original)} />
          </Labeled>
        );
      })}
    </div>
  );

  return (
    <form onSubmit={(event) => {
      event.preventDefault();
      if (dirty && !result.errors.length && !save.isPending) save.mutate();
    }}>
      <fieldset disabled={save.isPending} className="space-y-5">
        <section className={card}>
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Signing &amp; retention</h2>
          {numberFields('signing')}
          <p className="mt-3 text-xs text-slate-500">New download links use the retention period. Previously issued download links keep their expiry; increasing retention cannot restore deleted files.</p>
          {retentionReduced && <p role="status" className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">Reducing retention makes older existing files eligible for deletion at the next daily cleanup. Make sure the files you need have been downloaded before saving.</p>}
        </section>
        <section className={card}>
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Dates</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Labeled label="Timezone" hint="Used for dashboard dates and newly finalized documents. Search or enter an IANA timezone.">
              <TextInput list="platform-timezones" required value={dates.timezone} spellCheck={false}
                onChange={(event) => edit('timezone', event.target.value, settings.platform.timezone)} />
              <datalist id="platform-timezones">{zones.map((zone) => <option key={zone} value={zone} />)}</datalist>
            </Labeled>
            <Labeled label="Date format" hint="Completed documents keep the date format used in their signed PDF.">
              <Select value={dates.dateFormat} onChange={(event) => edit('dateFormat', event.target.value, settings.platform.dateFormat)}>
                {!DATE_FORMAT_OPTIONS.some((option) => option.value === settings.platform.dateFormat) &&
                  <option value={settings.platform.dateFormat} disabled>Current format: {settings.platform.dateFormat}</option>}
                {DATE_FORMAT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
            </Labeled>
          </div>
          <p className="mt-3 text-xs text-slate-500">Preview: {formatDateTime(new Date(), dates)}</p>
        </section>
        <section className={card}>
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Temporary uploads</h2>
          {numberFields('uploads')}
          {uploadReduced && <p role="status" className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">Older temporary uploads may be deleted at the next hourly cleanup. This also applies to uploads already waiting to be used.</p>}
        </section>
        <section className={card}>
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Webhook delivery</h2>
          {numberFields('webhooks')}
        </section>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!dirty || !!result.errors.length || save.isPending}>{save.isPending ? 'Saving…' : 'Save settings'}</Button>
          {dirty && <Button type="button" variant="secondary" onClick={() => { setDraft({}); save.reset(); setSaved(false); }}>Discard changes</Button>}
          {saved && <span role="status" className="text-sm text-emerald-700">Settings saved.</span>}
          {dirty && !save.isPending && <span className="text-xs text-slate-500">Unsaved changes</span>}
        </div>
        {!!result.errors.length && <div role="alert" className="text-sm text-red-600">{result.errors.map((error) => <p key={error}>{error}</p>)}</div>}
        {save.error && <p role="alert" className="text-sm text-red-600">{messageOf(save.error, 'Could not save settings')}</p>}
        <p className="text-xs text-slate-500">Saved settings take effect without a restart.</p>
      </fieldset>
    </form>
  );
}
